import { isNativeTab } from "../../utils/environment";
import { COOKIE_SDK_URLS, cookieConsentConfig } from "./cookieConfig";
import {
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  CookieConsentState,
  ProductConsent
} from "./types";

const DEFAULT_CONSENT: CookieConsentState = {
  isResolved: false,
  analytics: false,
  functional: false
};

function isProductionEnvironment(): boolean {
  const href = window.location.href;
  return (
    href.includes("accessacloud.com") &&
    !href.includes("preprod") &&
    !href.includes("test") &&
    !href.includes("staging")
  );
}

function getSdkScriptUrl(): string {
  return isProductionEnvironment()
    ? COOKIE_SDK_URLS.production
    : COOKIE_SDK_URLS.testing;
}

function loadSdkScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.AccessCookiePolicies) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.crossOrigin = "anonymous";
    script.src = getSdkScriptUrl();
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Access Cookie SDK script"));

    document.head.appendChild(script);
  });
}

function parseProductConsent(consent: ProductConsent): CookieConsentState {
  if (consent.consent === "*") {
    return { isResolved: true, analytics: true, functional: true };
  }

  if (consent.consent === null) {
    return { isResolved: true, analytics: false, functional: false };
  }

  const consentedPolicies = consent.consent.map((party) => party.policy);

  return {
    isResolved: true,
    analytics: consentedPolicies.includes("analytics"),
    functional: consentedPolicies.includes("functional")
  };
}

export async function initialiseIframeConsent(): Promise<CookieConsentState> {
  try {
    await loadSdkScript();

    if (!window.AccessCookiePolicies) {
      console.error("[CookieConsent] SDK loaded but not available on window");
      return DEFAULT_CONSENT;
    }

    const productConsent = await window.AccessCookiePolicies.SDK.tabInitialise({
      config: cookieConsentConfig,
      language: "en-GB"
    });

    return parseProductConsent(productConsent);
  } catch (error) {
    console.error("[CookieConsent] Failed to initialise SDK consent:", error);
    return DEFAULT_CONSENT;
  }
}

interface StoredConsent {
  version: string;
  analytics: boolean;
  functional: boolean;
  timestamp: number;
}

const CONSENT_EXPIRY_DAYS = 365;

export function getStoredConsent(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const stored: StoredConsent = JSON.parse(raw);

    if (stored.version !== CONSENT_VERSION) return null;

    const expiryMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - stored.timestamp > expiryMs) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    return {
      isResolved: true,
      analytics: stored.analytics,
      functional: stored.functional
    };
  } catch {
    return null;
  }
}

export function storeConsent(analytics: boolean, functional: boolean): void {
  const stored: StoredConsent = {
    version: CONSENT_VERSION,
    analytics,
    functional,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    console.error("[CookieConsent] Failed to store consent:", error);
  }
}

export function clearStoredConsent(): void {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Silently ignore storage errors
  }
}

export function isIframeMode(): boolean {
  return isNativeTab();
}
